import { useState } from "react";
import styled, { keyframes } from "styled-components";

const borderAnimation = keyframes`
  0% {    
    width: calc(100% + 2px);
    height: calc(100% - 1px);
    left: 0px;
    top: -1px;
    visibility: visible;
  }
  100% {
    width: calc(100% + -2px);
    height: calc(100% - 2px);
    left: 2px;
    top: 0px;
  }
`;

const Container = styled.button`
  width: 20px;
  height: 20px;

  vertical-align: middle;

  cursor: pointer;
  background: var(--color-light-0);
  border: 0px solid var(--color-main-blue);

  border-radius: 4px;

  position: relative;

  & .external-border {
    position: absolute;
    width: calc(100% + 2px);
    height: calc(100% - 1px);
    left: 0px;
    top: -1px;
    border: 1px solid var(--color-main-blue);
    border-radius: 4px;
    visibility: hidden;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 1px;
    left: 1px;
    margin-left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 2.5px;
  }

  &::before {
    background: var(--color-light-0);
    border: 1px solid var(--color-text-input);
  }

  &::after {
    background: var(--color-main-blue);
    border: 1px solid var(--color-main-blue);

    font-family: "unicons";
    color: var(--color-light-0);

    display: flex;
    align-items: center;
    justify-content: center;

    visibility: hidden;
    opacity: 0;
    transition: visibility 0.2s ease-in-out, opacity 0.2s ease-in-out;
  }

  &.active,
  &.inactive {
    & .external-border {
      animation: ${borderAnimation} 0.15s ease-in-out;
    }
  }

  &.active {
    &::after {
      content: "";
      visibility: visible;
      opacity: 1;
    }
  }

  &:hover {
    background: var(--color-light-0);
  }
`;

type TCheckProps = {
  onClick?: (
    isClicked: boolean,
    setClickState: React.Dispatch<
      React.SetStateAction<"" | "active" | "inactive">
    >
  ) => void;
  setSelectedRef?: (
    setClickState: React.Dispatch<
      React.SetStateAction<"" | "active" | "inactive">
    >,
    clickState: "" | "active" | "inactive"
  ) => void;
  initialState?: boolean;
  checked?: boolean; // controlled prop
  indeterminate?: boolean; // tri-state support
  id?: string;
  'data-testid'?: string;
};


const Check: React.FC<TCheckProps> = ({
  id,
  "data-testid": dataTestId,
  onClick,
  setSelectedRef,
  initialState,
  checked,
  indeterminate,
}) => {
  const [clickState, setClickState] = useState<"" | "active" | "inactive">(
    initialState !== undefined && initialState === true ? "active" : ""
  );

  // Determine the visual state: controlled if checked is provided, else internal state
  const isControlled = typeof checked === "boolean";

  // Only use "indeterminate" for visual rendering, not for state or setSelectedRef
  let visualState: "" | "active" | "indeterminate" = "";
  if (indeterminate) {
    visualState = "indeterminate";
  } else if (isControlled) {
    visualState = checked ? "active" : "";
  } else {
    // Only allow "" or "active" for visualState
    visualState = clickState === "active" ? "active" : "";
  }

  // setSelectedRef should never receive "indeterminate"
  if (setSelectedRef) setSelectedRef(setClickState, visualState === "indeterminate" ? "" : visualState);

  const clickHandler = () => {
    // For controlled, just call onClick; for uncontrolled, also update state
    if (onClick) {
      const willBeChecked = isControlled ? !checked : clickState !== "active";
      onClick(willBeChecked, setClickState);
    }
    if (!isControlled) {
      setClickState((prev) => {
        const value = prev === "active" ? "inactive" : "active";
        return value;
      });
    }
  };

  return (
    <Container
      id={id}
      role="button"
      className={visualState}
      onClick={clickHandler}
      data-testid={dataTestId}
    >
      <div className="external-border" key={visualState}></div>
      {visualState === "indeterminate" && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 12,
            height: 2,
            background: "var(--color-main-blue)",
            borderRadius: 2,
            transform: "translate(-50%, -50%)",
          }}
        />
      )}
    </Container>
  );
};

export default Check;
